import "@/App.css";
import { lazy, Suspense, Component } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Layout from "@/components/Layout";

// Depois de um deploy (ou com o servidor a reiniciar) um pedaço da app pode falhar a carregar:
// recarrega a página uma vez em vez de ficar o ecrã preto
const RELOAD_KEY = 'chunk-reload';
const lazyRetry = (load) => lazy(() => load().then(m => {
  try { sessionStorage.removeItem(RELOAD_KEY); } catch {}
  return m;
}).catch(err => {
  let tried = true;
  try { tried = !!sessionStorage.getItem(RELOAD_KEY); sessionStorage.setItem(RELOAD_KEY, '1'); } catch {}
  if (!tried) { window.location.reload(); return new Promise(() => {}); }
  throw err;
}));

// Qualquer erro inesperado mostra uma mensagem com botão em vez de ecrã preto
class AppErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('[App]', error, info); }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4 p-6 text-center" style={{ backgroundColor: 'var(--background-base, #16181C)', color: '#e5e7eb' }}>
        <p className="text-lg font-semibold">Algo correu mal ao carregar esta página.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 rounded-lg font-semibold text-white"
          style={{ backgroundColor: '#4A87D4' }}
        >
          Recarregar
        </button>
      </div>
    );
  }
}

const Login           = lazyRetry(() => import('@/pages/Login'));
const Dashboard       = lazyRetry(() => import('@/pages/Dashboard'));
const Products        = lazyRetry(() => import('@/pages/Products'));
const Customers       = lazyRetry(() => import('@/pages/Customers'));
const CustomerDetail  = lazyRetry(() => import('@/pages/CustomerDetail'));
const Orders          = lazyRetry(() => import('@/pages/Orders'));
const Orcamentos      = lazyRetry(() => import('@/pages/Orcamentos'));
const Suppliers       = lazyRetry(() => import('@/pages/Suppliers'));
const PriceLists      = lazyRetry(() => import('@/pages/PriceLists'));
const Activity        = lazyRetry(() => import('@/pages/Activity'));
const Admin           = lazyRetry(() => import('@/pages/Admin'));
const Inventario      = lazyRetry(() => import('@/pages/Inventario'));
const CatalogoPublico = lazyRetry(() => import('@/pages/CatalogoPublico'));
const Compras         = lazyRetry(() => import('@/pages/Compras'));
const Contagem        = lazyRetry(() => import('@/pages/Contagem'));

const PageLoader = () => (
  <div className="flex items-center justify-center h-screen" style={{ backgroundColor: 'var(--background-base, #16181C)' }}>
    <div className="spinner" />
  </div>
);

const ProtectedRoute = ({ children, permission }) => {
  const { user, loading, hasPermission } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (permission && !hasPermission(permission)) return <Navigate to="/painel" replace />;
  return children;
};

// QR das etiquetas: /q/<produto>?v=<variante> → abre o artigo na contagem de inventário
const QrRedirect = () => {
  const { pid } = useParams();
  const [sp] = useSearchParams();
  return <Navigate to={`/contagem?item=${pid}:${sp.get('v') || ''}`} replace />;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/painel" replace /> : <Login />} />
        <Route path="/catalogo" element={<CatalogoPublico />} />
        <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<Navigate to="/painel" replace />} />
          <Route path="painel" element={<Dashboard />} />
          <Route path="produtos" element={<Products />} />
          <Route path="clientes" element={<Customers />} />
          <Route path="clientes/:id" element={<CustomerDetail />} />
          <Route path="encomendas" element={<Orders />} />
          <Route path="orcamentos" element={<Orcamentos />} />
          <Route path="fornecedores" element={<Suppliers />} />
          <Route path="listas-precos" element={<PriceLists />} />
          <Route path="atividade" element={<Activity />} />
          <Route path="admin" element={<ProtectedRoute permission="manage_users"><Admin /></ProtectedRoute>} />
          <Route path="inventario" element={<ProtectedRoute permission="manage_inventory"><Inventario /></ProtectedRoute>} />
          <Route path="compras" element={<ProtectedRoute permission="manage_inventory"><Compras /></ProtectedRoute>} />
          <Route path="contagem" element={<ProtectedRoute permission="manage_inventory"><Contagem /></ProtectedRoute>} />
          <Route path="q/:pid" element={<QrRedirect />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <AppErrorBoundary>
        <BrowserRouter>
          <ThemeProvider>
            <AuthProvider>
              <AppRoutes />
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </AppErrorBoundary>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
